export const defaultCronEditor = {
  mode: "minutes",
  intervalMinutes: 30,
  startMinute: 0,
  intervalHours: 1,
  hourMinute: 0,
  dailyTime: "08:00",
  customExpression: "0 0/30 * * * ?",
};

function toNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function safeMinute(value) {
  return clamp(toNumber(value, 0), 0, 59);
}

function safeMinuteInterval(value) {
  return clamp(toNumber(value, 30), 1, 59);
}

function safeHourInterval(value) {
  return clamp(toNumber(value, 1), 1, 23);
}

function safeTime(value) {
  return /^\d{2}:\d{2}$/.test(value || "") ? value : "08:00";
}

export function buildCronExpression(editor) {
  if (editor.mode === "custom") {
    return editor.customExpression || "";
  }

  if (editor.mode === "hours") {
    return `0 ${safeMinute(editor.hourMinute)} 0/${safeHourInterval(editor.intervalHours)} * * ?`;
  }

  if (editor.mode === "daily") {
    const [hour, minute] = safeTime(editor.dailyTime).split(":");
    return `0 ${Number(minute)} ${Number(hour)} * * ?`;
  }

  return `0 ${safeMinute(editor.startMinute)}/${safeMinuteInterval(editor.intervalMinutes)} * * * ?`;
}

export function parseCronExpression(expression) {
  const normalized = (expression || "").trim();

  if (!normalized) {
    return { ...defaultCronEditor };
  }

  const parts = normalized.split(/\s+/);
  if (parts.length === 6) {
    const [seconds, minutes, hours, dayOfMonth, month, dayOfWeek] = parts;
    const commonDaily = seconds === "0" && dayOfMonth === "*" && month === "*" && dayOfWeek === "?";

    const everyMinutes = minutes.match(/^(\d{1,2})\/(\d{1,2})$/);
    if (commonDaily && everyMinutes && hours === "*") {
      return {
        ...defaultCronEditor,
        mode: "minutes",
        startMinute: safeMinute(everyMinutes[1]),
        intervalMinutes: safeMinuteInterval(everyMinutes[2]),
        customExpression: normalized,
      };
    }

    const everyHours = hours.match(/^(\d{1,2})\/(\d{1,2})$/);
    if (commonDaily && everyHours && /^\d{1,2}$/.test(minutes)) {
      return {
        ...defaultCronEditor,
        mode: "hours",
        hourMinute: safeMinute(minutes),
        intervalHours: safeHourInterval(everyHours[2]),
        customExpression: normalized,
      };
    }

    if (commonDaily && /^\d{1,2}$/.test(minutes) && /^\d{1,2}$/.test(hours)) {
      return {
        ...defaultCronEditor,
        mode: "daily",
        dailyTime: `${pad2(hours)}:${pad2(minutes)}`,
        customExpression: normalized,
      };
    }
  }

  return {
    ...defaultCronEditor,
    mode: "custom",
    customExpression: normalized,
  };
}

export function describeCronExpression(expression) {
  const editor = parseCronExpression(expression);

  if (editor.mode === "minutes") {
    const interval = safeMinuteInterval(editor.intervalMinutes);
    const start = safeMinute(editor.startMinute);
    const base = interval === 1 ? "A cada minuto" : `A cada ${interval} minutos`;
    return start === 0 ? base : `${base} a partir do minuto ${pad2(start)}`;
  }

  if (editor.mode === "hours") {
    const interval = safeHourInterval(editor.intervalHours);
    const minute = pad2(safeMinute(editor.hourMinute));
    return interval === 1 ? `De hora em hora no minuto ${minute}` : `A cada ${interval} horas no minuto ${minute}`;
  }

  if (editor.mode === "daily") {
    return `Todos os dias as ${safeTime(editor.dailyTime)}`;
  }

  return expression || "-";
}
